import React, { useState, useEffect } from 'react';
import { api } from '../api';
import ActionMenu from '../components/ActionMenu';
import { User } from '../types';
import { useNotification } from '../components/NotificationContext';

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const { notify } = useNotification();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'operator',
    is_admin: false,
    cantieri: [] as string[],
  });
  const [cantieriList, setCantieriList] = useState<string[]>([]);

  useEffect(() => {
    fetchUsers();
    fetchCantieriNames();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await api.get('/admin/users');
      setUsers(data.users);
    } catch (error: any) {
      notify(error.message || 'Failed to fetch users.', 'error');
    }
  };

  const fetchCantieriNames = async () => {
    try {
      const data = await api.get('/admin/cantieri');
      setCantieriList(data.cantieri.map((c: any) => c.name));
    } catch (error: any) {
      notify(error.message || 'Failed to fetch cantieri names.', 'error');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: (e.target as HTMLInputElement).checked });
    } else if (name === 'cantieri') {
      const options = (e.target as HTMLSelectElement).options;
      const selectedCantieri = [];
      for (let i = 0; i < options.length; i++) {
        if (options[i].selected) {
          selectedCantieri.push(options[i].value);
        }
      }
      setFormData({ ...formData, cantieri: selectedCantieri });
    }
    else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = e.target.value;
    setFormData({
      ...formData,
      role: newRole,
      is_admin: newRole === 'admin'
    });
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    const userRole = user.role || (user.is_admin ? 'admin' : 'operator');
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: userRole,
      is_admin: userRole === 'admin',
      cantieri: user.cantieri,
    });
  };

  const handleDelete = async (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/admin/users/${userId}`);
        notify('Utente eliminato con successo!', 'success');
        fetchUsers();
      } catch (error: any) {
        notify(error.message || 'Errore eliminazione utente.', 'error');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.put(`/admin/users/${editingUser.id}`, formData);
        notify('Utente aggiornato con successo!', 'success');
      } else {
        await api.post('/admin/users', formData);
        notify('Utente creato con successo!', 'success');
      }
      setFormData({ username: '', email: '', password: '', role: 'operator', is_admin: false, cantieri: [] });
      setEditingUser(null);
      fetchUsers();
    } catch (error: any) {
      notify(error.message || 'Errore salvataggio utente.', 'error');
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h2 style={{ marginBottom: '20px' }}>{editingUser ? 'Edit User' : 'Manage Users'}</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div className="form-group">
              <label>Username</label>
              <input type="text" name="username" value={formData.username} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} required />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div className="form-group">
              <label>Password (leave blank to keep current)</label>
              <input type="password" name="password" value={formData.password} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Ruolo Utente</label>
              <select name="role" value={formData.role} onChange={handleRoleChange} style={{ width: '100%' }}>
                <option value="operator">Operatore</option>
                <option value="admin">Amministratore</option>
                <option value="client">Cliente</option>
              </select>
              {formData.role === 'client' && (
                <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
                  Nota: L'utente Cliente visualizza solo i cantieri assegnati in sola consultazione.
                </div>
              )}
            </div>
          </div>
          <div className="form-group">
            <label>Assign Cantieri (Select multiple)</label>
            <select name="cantieri" multiple={true} value={formData.cantieri} onChange={handleInputChange} style={{ height: '100px' }}>
              {cantieriList.map((cantiereName) => (
                <option key={cantiereName} value={cantiereName}>{cantiereName}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button type="submit" className="btn btn-primary">{editingUser ? 'Update User' : 'Create User'}</button>
            {editingUser && <button type="button" className="btn btn-text" onClick={() => { setEditingUser(null); setFormData({ username: '', email: '', password: '', role: 'operator', is_admin: false, cantieri: [] }); }}>Cancel</button>}
          </div>
        </form>
      </div>

      <div className="card">
        <h3>Existing Users</h3>
        <table className="table-responsive">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Ruolo</th>
              <th>Cantieri Assegnati</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const userRole = user.role || (user.is_admin ? 'admin' : 'operator');
              return (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>
                    <span style={{
                      padding: '3px 8px',
                      borderRadius: '4px',
                      fontSize: '0.78rem',
                      fontWeight: '600',
                      background: userRole === 'admin' ? '#e8eaf6' : userRole === 'client' ? '#fff3e0' : '#e0f2f1',
                      color: userRole === 'admin' ? '#283593' : userRole === 'client' ? '#e65100' : '#00695c',
                    }}>
                      {userRole === 'admin' ? 'Admin' : userRole === 'client' ? 'Cliente' : 'Operatore'}
                    </span>
                  </td>
                  <td>{user.cantieri.length > 0 ? user.cantieri.join(', ') : <span style={{ color: '#aaa', fontStyle: 'italic' }}>Nessun cantiere</span>}</td>
                  <td>
                    <ActionMenu actions={[
                      { label: 'Modifica', icon: 'fas fa-pen-to-square', onClick: () => handleEdit(user) },
                      { label: 'Elimina', icon: 'fas fa-trash-can', onClick: () => handleDelete(user.id), color: 'var(--error-color)' },
                    ]} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminUsers;